import React, { useEffect, useState } from "react";
import api from "../../../../services/api";
import { renderError } from "../../../../helpers/errors";
import moment from "moment";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import Icon from "../../../../components/Icon";
import { getUser } from "../../../../services/auth";
import { ModalDelete } from "../../../../components/ModalDelete";
import InputDate from "../../../../components/InputDate";
import Input from "../../../../components/Input";
import InputTextarea from "../../../../components/InputTextarea";

const typeOptions = [
    {label: "Ligação", value: "call"},
    {label: "Reunião", value: "meeting"},
    {label: "E-mail", value: "email"},
    {label: "Tarefa", value: "task"}
];

const statusOptions = [
    {label: "Pendente", value: "pending"},
    {label: "Concluída", value: "completed"}
];

const taskPendingDesc = {
    call: <><b>Ligar</b> para o contato</>,
    meeting: <><b>Reunião</b> com contato</>,
    email: <><b>E-mail</b> para o contato</>,
    task: <>Realizar <b>tarefa</b></>,
};

const taskCompletedDesc = {
    call: 'Ligação realizada',
    meeting: 'Reunião realizada',
    email: 'E-mail enviado',
    task: 'Tarefa realizada',
};

const getTaskMoment = task =>
    moment(`${task.date} ${task.time || '23:59'}`, 'YYYY-MM-DD HH:mm:ss');

const formatTaskDate = task => {
    const day = moment(task.date, 'YYYY-MM-DD');
    const time = task.time ? moment(task.time, 'HH:mm:ss').format('HH:mm') : null;

    let dayLabel;
    if(day.isSame(moment(), 'day')) dayLabel = 'Hoje';
    else if(day.isSame(moment().add(1, 'day'), 'day')) dayLabel = 'Amanhã';
    else if(day.isSame(moment().subtract(1, 'day'), 'day')) dayLabel = 'Ontem';
    else dayLabel = day.format('DD/MM/YYYY');

    return time ? `${dayLabel} às ${time}` : dayLabel;
};

export const CRMTasks = ({cardId}) => {
    const [tasks, setTasks] = useState([]);
    const [gettingTasks, setGettingTasks] = useState(true);

    const [taskToEdit, setTaskToEdit] = useState(null);
    const [updatingTask, setUpdatingTask] = useState(false);
    const [togglingTaskId, setTogglingTaskId] = useState(null);
    const [taskIdToDelete, setTaskIdToDelete] = useState(null);

    const user = getUser();

    useEffect(() => {
        if(cardId){
            getTasks();
        }
    }, [cardId]);

    const getTasks = () => {
        setGettingTasks(true);

        api.get(`/pipeline-stage-card-task?pipeline_stage_card_id=${cardId}`).then(res => {
            setTasks(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).then(() => setGettingTasks(false));
    }

    const createTask = () => {
        setTaskToEdit({
            id: null,
            pipeline_stage_card_id: cardId,
            type: 'call',
            status: 'pending',
            content: '',
            admin_id: user.id,
            admin_name: user.name,
            admin_email: user.email,
            date: moment().format('YYYY-MM-DD'),
            time: ''
        });
    }

    const updateTask = () => {
        setUpdatingTask(true);

        api[taskToEdit.id ? 'put' : 'post'](`/pipeline-stage-card-task/${taskToEdit.id ?? ''}`, taskToEdit).then(res => {
            setTaskToEdit(null);
            getTasks();
        }).catch(error => {
            renderError(error);
        }).then(() => setUpdatingTask(false));
    }

    const toggleTaskStatus = task => {
        const payload = task.status === 'pending'
            ? {
                ...task,
                status: 'completed',
                date: moment().format('YYYY-MM-DD'),
                time: moment().format('HH:mm')
            }
            : {...task, status: 'pending'};

        setTogglingTaskId(task.id);

        api.put(`/pipeline-stage-card-task/${task.id}`, payload).then(res => {
            getTasks();
        }).catch(error => {
            renderError(error);
        }).then(() => setTogglingTaskId(null));
    }

    const deleteTask = () => {
        setUpdatingTask(true);

        api.delete(`/pipeline-stage-card-task/${taskIdToDelete}`).then(res => {
            setTaskIdToDelete(null);
            setTaskToEdit(null);
            getTasks();
        }).catch(error => {
            renderError(error)
        }).then(() => setUpdatingTask(false));
    }

    const now = moment();
    const pendingTasks = tasks.filter(task => task.status === 'pending');
    const overdueTasks = pendingTasks
        .filter(task => getTaskMoment(task).isBefore(now))
        .sort((a, b) => getTaskMoment(a) - getTaskMoment(b));
    const upcomingTasks = pendingTasks
        .filter(task => !getTaskMoment(task).isBefore(now))
        .sort((a, b) => getTaskMoment(a) - getTaskMoment(b));
    const completedTasks = tasks
        .filter(task => task.status === 'completed')
        .sort((a, b) => getTaskMoment(b) - getTaskMoment(a));

    const sections = [
        {key: 'overdue', title: 'Atrasadas', tasks: overdueTasks},
        {key: 'upcoming', title: 'Pendentes', tasks: upcomingTasks},
        {key: 'completed', title: 'Concluídas', tasks: completedTasks}
    ].filter(section => section.tasks.length > 0);

    const renderTask = task => {
        const isCompleted = task.status === 'completed';
        const isOverdue = !isCompleted && getTaskMoment(task).isBefore(now);
        const isToggling = togglingTaskId === task.id;

        return (
            <div key={task.id} className={`task${isCompleted ? ' is-completed' : ''}`}>
                <button
                    className={`check${isCompleted ? ' checked' : ''}`}
                    title={isCompleted ? 'Reabrir tarefa' : 'Marcar como realizada'}
                    disabled={isToggling}
                    onClick={() => toggleTaskStatus(task)}
                >
                    {isToggling && <Icon name={`loading`} size={20} className={`check-loading`} />}
                    {!isToggling && isCompleted && <Icon name={`task-completed`} size={20} />}
                    {!isToggling && !isCompleted &&
                        <>
                            <Icon name={`uncheck`} size={20} className={`check-idle`} />
                            <Icon name={`task-completed`} size={20} className={`check-hover`} />
                        </>
                    }
                </button>
                <span className={`type-chip type-${task.type}`} title={typeOptions.find(option => option.value === task.type)?.label}>
                    <Icon name={`task-${task.type}-${task.status}`} size={16} />
                </span>
                <div className="details">
                    <div className="data">
                        <span className="desc">
                            {isCompleted
                                ? <><b>{taskCompletedDesc[task.type]}</b> por {task.admin_name}</>
                                : taskPendingDesc[task.type]
                            }
                        </span>
                        <span className={`date${isOverdue ? ' overdue' : ''}`}>
                            {isOverdue && <Icon name={`alert`} size={13} />}
                            {formatTaskDate(task)}
                        </span>
                        {task.content &&
                            <p className="content">{task.content}</p>
                        }
                    </div>
                </div>
                <div className="actions">
                    <button
                        className="action action-edit"
                        title={`Editar`}
                        onClick={() => setTaskToEdit(task)}
                    >
                        <Icon name={`edit`} size={16} />
                    </button>
                    <button
                        className="action action-delete"
                        title={`Excluir`}
                        onClick={() => setTaskIdToDelete(task.id)}
                    >
                        <Icon name={`delete`} size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {taskToEdit !== null &&
                <Modal
                    show={true}
                    title={`${taskToEdit.id ? 'Editar' : 'Criar'} tarefa`}
                    close={() => setTaskToEdit(null)}
                    size={`small`}
                    footer={
                        <>
                            {taskToEdit.id &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`delete-small`}
                                    text={`Excluir`}
                                    action={() => setTaskIdToDelete(taskToEdit.id)}
                                    loading={updatingTask}
                                />
                            }
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Salvar alterações`}
                                svg={`save-white`}
                                action={updateTask}
                                loading={updatingTask}
                            />
                        </>
                    }
                >
                    <div className="section task-form">
                        <span className="picker-label">Tipo da tarefa *</span>
                        <div className="task-type-picker">
                            {typeOptions.map(option => (
                                <button
                                    key={option.value}
                                    className={taskToEdit.type === option.value ? 'active' : ''}
                                    onClick={() => setTaskToEdit(prev => ({...prev, type: option.value}))}
                                >
                                    <Icon name={`task-${option.value}-pending`} size={15} />
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <div className="field-row">
                            <InputDate
                                label={`Data *`}
                                value={taskToEdit.date}
                                change={value => setTaskToEdit(prev => ({...prev, date: value}))}
                                format={`YYYY-MM-DD`}
                            />
                            <Input
                                label={`Hora *`}
                                value={taskToEdit.time}
                                mask={`convertToHour`}
                                change={value => setTaskToEdit(prev => ({...prev, time: value}))}
                                placeholder={`00:00`}
                            />
                        </div>
                        <InputTextarea
                            label={`Observações`}
                            value={taskToEdit.content}
                            change={value => setTaskToEdit(prev => ({...prev, content: value}))}
                        />
                        {taskToEdit.id &&
                            <>
                                <span className="picker-label">Status</span>
                                <div className="task-type-picker task-status-picker">
                                    {statusOptions.map(option => (
                                        <button
                                            key={option.value}
                                            className={taskToEdit.status === option.value ? 'active' : ''}
                                            onClick={() => setTaskToEdit(prev => ({...prev, status: option.value}))}
                                        >
                                            <Icon name={option.value === 'completed' ? 'task-completed' : 'uncheck'} size={15} />
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </>
                        }
                    </div>
                </Modal>
            }
            <ModalDelete
                show={taskIdToDelete !== null}
                close={() => setTaskIdToDelete(null)}
                action={deleteTask}
                loading={updatingTask}
            />
            <div className="tasks">
                <div className="toolbar">
                    <div className="summary">
                        {!gettingTasks && tasks.length > 0 &&
                            <>
                                {overdueTasks.length > 0 &&
                                    <span className="pill pill-overdue">
                                        <Icon name={`alert`} size={13} />
                                        {overdueTasks.length} atrasada{overdueTasks.length === 1 ? '' : 's'}
                                    </span>
                                }
                                {upcomingTasks.length > 0 &&
                                    <span className="pill">
                                        {upcomingTasks.length} pendente{upcomingTasks.length === 1 ? '' : 's'}
                                    </span>
                                }
                                {completedTasks.length > 0 &&
                                    <span className="pill pill-completed">
                                        <Icon name={`task-completed`} size={13} />
                                        {completedTasks.length} concluída{completedTasks.length === 1 ? '' : 's'}
                                    </span>
                                }
                            </>
                        }
                    </div>
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`plus-white`}
                        text={`Nova tarefa`}
                        action={createTask}
                    />
                </div>
                <div className="list">
                    {gettingTasks &&
                        <>
                            {[0, 1, 2].map(i => (
                                <div key={i} className="task-skeleton">
                                    <span className="sk sk-circle" />
                                    <span className="sk sk-chip" />
                                    <div className="sk-lines">
                                        <span className="sk sk-line" />
                                        <span className="sk sk-line short" />
                                    </div>
                                </div>
                            ))}
                        </>
                    }
                    {!gettingTasks && tasks.length === 0 &&
                        <div className="empty-state">
                            <span className="empty-icon">
                                <Icon name={`task-task-pending`} size={26} />
                            </span>
                            <strong>Nenhuma tarefa por aqui</strong>
                            <p>Crie tarefas de ligação, reunião ou e-mail para não perder o próximo passo desta oportunidade.</p>
                            <Button
                                type={`secondary`}
                                size={`small`}
                                svg={`plus-blue`}
                                text={`Criar primeira tarefa`}
                                action={createTask}
                            />
                        </div>
                    }
                    {!gettingTasks && sections.map(section => (
                        <div key={section.key} className={`task-section section-${section.key}`}>
                            <div className="section-title">
                                <span>{section.title}</span>
                                <em>{section.tasks.length}</em>
                            </div>
                            {section.tasks.map(renderTask)}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

export default CRMTasks;
